import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  IonBadge,
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonContent,
  IonDatetime,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonSegment,
  IonSegmentButton,
  IonSpinner,
  IonTextarea,
  IonToast,
  IonPage,
  IonText,
} from "@ionic/react";
import {
  cameraOutline,
  imageOutline,
  trashOutline,
  locationOutline,
  checkmarkCircleOutline,
  alertCircleOutline,
  closeCircleOutline,
  removeCircleOutline,
  ellipseOutline,
} from "ionicons/icons";
import Header from "src/components/Header";
import { getCurrentPosition, reverseGeocode } from "src/utils/geo";

// Mapeo de cada opcion de respuesta a su color del tema y su icono.
// Usa los colores semanticos estandar de la app (success/warning/danger/medium).
const META_OPCION: Record<string, { color: string; icon: string }> = {
  BUEN_ESTADO: { color: "success", icon: checkmarkCircleOutline },
  SI: { color: "success", icon: checkmarkCircleOutline },
  CON_DETALLES: { color: "warning", icon: alertCircleOutline },
  NO: { color: "danger", icon: closeCircleOutline },
  NO_APLICA: { color: "medium", icon: removeCircleOutline },
};
import {
  listarPreguntasReconocimiento,
  listarOpcionesReconocimiento,
  obtenerContextoReconocimiento,
  crearReconocimiento,
  listarReconocimientos,
  PreguntaReconocimiento,
  OpcionReconocimiento,
  ContextoReconocimiento,
  ReconocimientoListItem,
  FotoReconocimientoPayload,
} from "src/services/evaluacionReconocimiento";
import { fetchPuntoServicio, IfetchPuntoServicio } from "src/services/PedidoDeProducto/pedido.producto.services";

const normalizarFecha = (value: string | string[] | null | undefined) => {
  if (!value) return undefined;
  const raw = Array.isArray(value) ? value[0] : value;
  if (!raw) return undefined;
  return raw.toString().slice(0, 10);
};

const EvaluacionReconocimiento: React.FC = () => {
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [toast, setToast] = useState<{ show: boolean; msg: string; color?: string }>({ show: false, msg: "" });

  const [preguntas, setPreguntas] = useState<PreguntaReconocimiento[]>([]);
  const [opciones, setOpciones] = useState<Record<string, OpcionReconocimiento[]>>({});

  // Modo de PS: existente (buscador) o prospecto (a mano)
  const [esProspecto, setEsProspecto] = useState(false);

  // PS existente
  const [psBusqueda, setPsBusqueda] = useState("");
  const [psResultados, setPsResultados] = useState<IfetchPuntoServicio[]>([]);
  const [psBuscando, setPsBuscando] = useState(false);
  const [psErrorBusqueda, setPsErrorBusqueda] = useState<string | null>(null);
  const [psSeleccionado, setPsSeleccionado] = useState<IfetchPuntoServicio | null>(null);
  const [contexto, setContexto] = useState<ContextoReconocimiento | null>(null);

  // Prospecto
  const [prospectoNombre, setProspectoNombre] = useState("");
  const [prospectoDireccion, setProspectoDireccion] = useState("");
  const [prospectoContacto, setProspectoContacto] = useState("");
  const [latitud, setLatitud] = useState<number | undefined>(undefined);
  const [longitud, setLongitud] = useState<number | undefined>(undefined);
  const [obteniendoUbicacion, setObteniendoUbicacion] = useState(false);

  const [fechaVisita, setFechaVisita] = useState<string | undefined>(undefined);

  // Respuestas y comentarios por pregunta
  const [respuestas, setRespuestas] = useState<Record<number, string>>({});
  const [comentarios, setComentarios] = useState<Record<number, string>>({});

  // Opcionales
  const [planAccion, setPlanAccion] = useState("");
  const [fechaPlan, setFechaPlan] = useState<string | undefined>(undefined);
  const [comentarioGeneral, setComentarioGeneral] = useState("");
  const [fotos, setFotos] = useState<FotoReconocimientoPayload[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [registros, setRegistros] = useState<ReconocimientoListItem[]>([]);
  const [listando, setListando] = useState(false);

  const preguntasEstructura = useMemo(
    () =>
      preguntas
        .filter((p) => (p.seccion || "").toUpperCase() === "ESTRUCTURA")
        .sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0)),
    [preguntas]
  );
  const preguntasRelevamiento = useMemo(
    () =>
      preguntas
        .filter((p) => (p.seccion || "").toUpperCase() === "RELEVAMIENTO")
        .sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0)),
    [preguntas]
  );

  // Cargar catalogo
  useEffect(() => {
    let activo = true;
    const cargar = async () => {
      setCargando(true);
      try {
        const [preg, opc] = await Promise.all([
          listarPreguntasReconocimiento(),
          listarOpcionesReconocimiento(),
        ]);
        if (!activo) return;
        setPreguntas(preg);
        const mapOpciones: Record<string, OpcionReconocimiento[]> = {};
        opc.forEach((o) => {
          const tipo = (o.tipo_respuesta || "").toUpperCase();
          if (!tipo) return;
          if (!mapOpciones[tipo]) mapOpciones[tipo] = [];
          mapOpciones[tipo].push(o);
        });
        Object.keys(mapOpciones).forEach((k) => {
          mapOpciones[k].sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0));
        });
        setOpciones(mapOpciones);
      } catch (error: any) {
        setToast({ show: true, msg: error?.message || "No se pudo cargar la configuracion.", color: "danger" });
      } finally {
        if (activo) setCargando(false);
      }
    };
    cargar();
    return () => {
      activo = false;
    };
  }, []);

  // Buscador de PS existente
  useEffect(() => {
    let cancelado = false;
    const termino = psBusqueda.trim();
    if (esProspecto || termino.length < 3) {
      setPsResultados([]);
      setPsErrorBusqueda(null);
      setPsBuscando(false);
      return;
    }
    setPsBuscando(true);
    (async () => {
      try {
        const res = await fetchPuntoServicio(termino);
        if (cancelado) return;
        if (res.error) {
          setPsResultados([]);
          setPsErrorBusqueda(res.error);
        } else {
          setPsResultados(res.datos || []);
          setPsErrorBusqueda(null);
        }
      } catch {
        if (!cancelado) setPsErrorBusqueda("No se pudo buscar el Punto de Servicio");
      } finally {
        if (!cancelado) setPsBuscando(false);
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [psBusqueda, esProspecto]);

  // Contexto del PS existente (autocompletar fiscal)
  useEffect(() => {
    let activo = true;
    const cargarContexto = async () => {
      if (esProspecto || !psSeleccionado?.id) {
        setContexto(null);
        return;
      }
      try {
        const ctx = await obtenerContextoReconocimiento(
          psSeleccionado.id,
          psSeleccionado.ps_codigo ?? psSeleccionado.objeto
        );
        if (activo) setContexto(ctx);
      } catch {
        if (activo) setContexto(null);
      }
    };
    cargarContexto();
    return () => {
      activo = false;
    };
  }, [psSeleccionado, esProspecto]);

  const cargarRegistros = async (psId?: number) => {
    setListando(true);
    try {
      const data = await listarReconocimientos({ ps_id: psId, limit: 20, offset: 0 });
      setRegistros(data);
    } catch {
      setRegistros([]);
    } finally {
      setListando(false);
    }
  };

  useEffect(() => {
    cargarRegistros(esProspecto ? undefined : psSeleccionado?.id);
  }, [psSeleccionado?.id, esProspecto]);

  const seleccionarPuntoServicio = (ps: IfetchPuntoServicio) => {
    setPsSeleccionado(ps);
    setPsBusqueda("");
    setPsResultados([]);
    setPsErrorBusqueda(null);
  };

  const limpiarSeleccionPs = () => {
    setPsSeleccionado(null);
    setContexto(null);
    setPsBusqueda("");
    setPsResultados([]);
    setPsErrorBusqueda(null);
  };

  const cambiarModo = (prospecto: boolean) => {
    setEsProspecto(prospecto);
    limpiarSeleccionPs();
    setLatitud(undefined);
    setLongitud(undefined);
  };

  const actualizarRespuesta = (preguntaId: number, codigo: string) => {
    setRespuestas((prev) => ({ ...prev, [preguntaId]: codigo }));
  };
  const actualizarComentario = (preguntaId: number, texto: string) => {
    setComentarios((prev) => ({ ...prev, [preguntaId]: texto }));
  };

  const obtenerUbicacion = async () => {
    setObteniendoUbicacion(true);
    try {
      const { lat, lng } = await getCurrentPosition();
      setLatitud(Number(lat));
      setLongitud(Number(lng));
      // Bonus: si la direccion esta vacia, la autocompletamos desde las coordenadas.
      if (!prospectoDireccion.trim()) {
        try {
          const geo = await reverseGeocode(lat, lng);
          if (geo?.direccion) setProspectoDireccion(geo.direccion);
        } catch {
          // El reverse geocoding es opcional; si falla igual quedan las coordenadas.
        }
      }
      setToast({ show: true, msg: "Ubicacion obtenida.", color: "success" });
    } catch (error: any) {
      setToast({ show: true, msg: error?.message || "No se pudo obtener la ubicacion.", color: "warning" });
    } finally {
      setObteniendoUbicacion(false);
    }
  };

  const onSeleccionarFotos = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    const nuevas: FotoReconocimientoPayload[] = [];
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      if (!f.type.startsWith("image/")) continue;
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ""));
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(f);
      }).catch(() => "");
      if (dataUrl) {
        nuevas.push({ data_url: dataUrl, nombre_archivo: f.name, mime_type: f.type });
      }
    }
    if (nuevas.length) setFotos((prev) => [...prev, ...nuevas]);
    if (event.target) event.target.value = "";
  };

  const eliminarFoto = (idx: number) => {
    setFotos((prev) => prev.filter((_, i) => i !== idx));
  };

  const renderPregunta = (p: PreguntaReconocimiento, nro: number) => {
    const tipo = (p.tipo_respuesta || "").toUpperCase();
    const opcionesTipo = opciones[tipo] || [];
    const seleccion = respuestas[p.id] || "";
    return (
      <div
        key={p.id}
        style={{
          padding: "14px 0",
          borderBottom: "2px solid rgba(var(--ion-color-medium-rgb), 0.55)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <span
            style={{
              background: "var(--ion-color-primary)",
              color: "var(--ion-color-primary-contrast)",
              borderRadius: "50%",
              width: 26,
              height: 26,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 13,
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            {nro}
          </span>
          <span style={{ fontWeight: 600, fontSize: 15, color: "var(--ion-color-dark)" }}>
            {p.pregunta}
          </span>
        </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "nowrap", marginTop: 10, marginBottom: 6 }}>
            {opcionesTipo.map((o) => {
              const codigoUp = (o.codigo || "").toUpperCase();
              const meta = META_OPCION[codigoUp] || { color: "medium", icon: ellipseOutline };
              const activa = seleccion === o.codigo;
              return (
                <button
                  type="button"
                  key={o.codigo}
                  onClick={() => actualizarRespuesta(p.id, o.codigo)}
                  style={{
                    flex: "1 1 0",
                    minWidth: 0,
                    cursor: "pointer",
                    padding: "12px 8px",
                    borderRadius: 12,
                    border: activa
                      ? `2px solid var(--ion-color-${meta.color})`
                      : "1px solid rgba(var(--ion-color-medium-rgb), 0.45)",
                    background: activa
                      ? `rgba(var(--ion-color-${meta.color}-rgb), 0.12)`
                      : "rgba(var(--ion-color-medium-rgb), 0.07)",
                    color: activa ? `var(--ion-color-${meta.color})` : "var(--ion-color-medium)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 6,
                    transition: "all 0.15s ease",
                  }}
                >
                  <IonIcon icon={meta.icon} style={{ fontSize: 24 }} />
                  <span style={{ fontSize: 13, fontWeight: activa ? 700 : 500, textAlign: "center" }}>
                    {o.etiqueta}
                  </span>
                </button>
              );
            })}
          </div>
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, color: "var(--ion-color-dark)" }}>
              Comentarios
            </div>
            <div
              style={{
                border: "1px solid rgba(var(--ion-color-medium-rgb), 0.4)",
                borderRadius: 8,
                background: "rgba(var(--ion-color-medium-rgb), 0.06)",
                padding: "0 10px",
              }}
            >
              <IonTextarea
                autoGrow
                placeholder="Escribe un comentario (opcional)"
                value={comentarios[p.id] || ""}
                onIonChange={(e) => actualizarComentario(p.id, String(e.detail.value || ""))}
                style={{ "--padding-start": "0px", "--padding-top": "6px", "--padding-bottom": "6px" }}
              />
            </div>
          </div>
        </div>
    );
  };

  const onGuardar = async () => {
    if (!esProspecto && !psSeleccionado?.id) {
      setToast({ show: true, msg: "Selecciona un Punto de Servicio.", color: "warning" });
      return;
    }
    if (esProspecto && !prospectoNombre.trim()) {
      setToast({ show: true, msg: "Ingresa el nombre del prospecto.", color: "warning" });
      return;
    }
    if (!fechaVisita) {
      setToast({ show: true, msg: "Selecciona la Fecha de Visita.", color: "warning" });
      return;
    }
    const todas = [...preguntasEstructura, ...preguntasRelevamiento];
    const faltantes = todas.filter((p) => !respuestas[p.id]);
    if (faltantes.length > 0) {
      setToast({ show: true, msg: "Responde todas las preguntas (puedes usar 'No Aplica').", color: "warning" });
      return;
    }

    setGuardando(true);
    try {
      const payload = {
        es_prospecto: esProspecto,
        ps_id: esProspecto ? undefined : psSeleccionado?.id,
        ps_codigo: esProspecto ? undefined : String(psSeleccionado?.ps_codigo || psSeleccionado?.objeto || ""),
        ps_nombre: esProspecto ? prospectoNombre.trim() : psSeleccionado?.ps_nombre || "",
        prospecto_direccion: esProspecto ? prospectoDireccion.trim() || undefined : undefined,
        prospecto_contacto: esProspecto ? prospectoContacto.trim() || undefined : undefined,
        latitud: esProspecto ? latitud : undefined,
        longitud: esProspecto ? longitud : undefined,
        fecha_visita: fechaVisita,
        fiscal: !esProspecto && contexto?.fiscal ? contexto.fiscal : undefined,
        plan_accion: planAccion.trim() || undefined,
        fecha_plan_accion: fechaPlan || undefined,
        comentario_general: comentarioGeneral.trim() || undefined,
        respuestas: todas.map((p) => ({
          pregunta_id: p.id,
          respuesta_codigo: respuestas[p.id],
          comentario: (comentarios[p.id] || "").trim() || undefined,
        })),
        fotos: fotos.length ? fotos : undefined,
      };
      await crearReconocimiento(payload);
      setToast({ show: true, msg: "Reconocimiento guardado.", color: "success" });
      // Reset
      setRespuestas({});
      setComentarios({});
      setPlanAccion("");
      setFechaPlan(undefined);
      setComentarioGeneral("");
      setFotos([]);
      setProspectoNombre("");
      setProspectoDireccion("");
      setProspectoContacto("");
      setLatitud(undefined);
      setLongitud(undefined);
      setFechaVisita(undefined);
      await cargarRegistros(esProspecto ? undefined : psSeleccionado?.id);
    } catch (error: any) {
      setToast({ show: true, msg: error?.message || "No se pudo guardar el reconocimiento.", color: "danger" });
    } finally {
      setGuardando(false);
    }
  };

  return (
    <IonPage>
      <Header titulo="Visitas de Reconocimiento" />
      <IonContent
        id="evaluacion-reconocimiento"
        style={{
          "--padding-start": "6px",
          "--padding-end": "6px",
          "--padding-top": "12px",
          "--padding-bottom": "12px",
        }}
      >
        {cargando && (
          <div className="ion-padding">
            <IonSpinner />
          </div>
        )}

        <IonCard>
          <IonCardHeader>
            <IonCardTitle color="primary">Datos de la visita</IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            <IonSegment
              value={esProspecto ? "prospecto" : "existente"}
              onIonChange={(e) => cambiarModo(String(e.detail.value) === "prospecto")}
            >
              <IonSegmentButton value="existente">
                <IonLabel>PS existente</IonLabel>
              </IonSegmentButton>
              <IonSegmentButton value="prospecto">
                <IonLabel>Prospecto nuevo</IonLabel>
              </IonSegmentButton>
            </IonSegment>

            <IonList>
              {!esProspecto ? (
                <>
                  <IonItem lines="full">
                    <IonLabel className="ion-text-wrap">
                      <strong>Punto de Servicio</strong>
                      {psSeleccionado ? (
                        <>
                          <div>{psSeleccionado.ps_nombre || "Sin nombre"}</div>
                          <div className="ps-objeto">Codigo: {psSeleccionado.ps_codigo || psSeleccionado.objeto}</div>
                        </>
                      ) : (
                        <div>Busca y selecciona un punto de servicio.</div>
                      )}
                    </IonLabel>
                    {psSeleccionado && (
                      <IonButton slot="end" fill="clear" onClick={limpiarSeleccionPs}>
                        Limpiar
                      </IonButton>
                    )}
                  </IonItem>

                  <IonItem>
                    <IonLabel position="stacked">Buscar Punto de Servicio</IonLabel>
                    <IonInput
                      clearInput
                      value={psBusqueda}
                      placeholder="Ingresa al menos 3 letras"
                      onIonChange={(e) => setPsBusqueda((e.detail.value ?? "").toString())}
                    />
                  </IonItem>

                  <IonItem lines="none">
                    <IonLabel className="ion-text-wrap">
                      {psErrorBusqueda ? (
                        <IonText color="danger">{psErrorBusqueda}</IonText>
                      ) : psBusqueda.trim().length === 0 ? (
                        <IonText color="medium">Ingresa al menos 3 letras para buscar.</IonText>
                      ) : psBusqueda.trim().length < 3 ? (
                        <IonText color="medium">Escribe {3 - psBusqueda.trim().length} letra(s) mas.</IonText>
                      ) : psResultados.length === 0 && !psBuscando ? (
                        <IonText color="warning">Sin resultados para "{psBusqueda}".</IonText>
                      ) : (
                        <IonText color="medium">Selecciona un resultado.</IonText>
                      )}
                    </IonLabel>
                    {psBuscando && <IonSpinner slot="end" />}
                  </IonItem>

                  {psResultados.length > 0 &&
                    psResultados.map((ps) => (
                      <IonItem
                        key={ps.id}
                        button
                        detail={false}
                        onClick={() => seleccionarPuntoServicio(ps)}
                        color={psSeleccionado?.id === ps.id ? "light" : undefined}
                      >
                        <IonLabel>
                          <strong>{ps.ps_nombre || "Sin nombre"}</strong>
                          <div>{ps.ps_codigo || ps.objeto}</div>
                        </IonLabel>
                      </IonItem>
                    ))}

                  <IonItem>
                    <IonLabel position="stacked">Fiscal de Operaciones</IonLabel>
                    <IonInput value={contexto?.fiscal?.nombre || ""} readonly />
                  </IonItem>
                </>
              ) : (
                <>
                  <IonItem>
                    <IonLabel position="stacked">Nombre del prospecto / lugar *</IonLabel>
                    <IonInput
                      value={prospectoNombre}
                      placeholder="Nombre del nuevo PS"
                      onIonChange={(e) => setProspectoNombre(String(e.detail.value || ""))}
                    />
                  </IonItem>
                  <IonItem>
                    <IonLabel position="stacked">Direccion</IonLabel>
                    <IonInput
                      value={prospectoDireccion}
                      placeholder="Opcional"
                      onIonChange={(e) => setProspectoDireccion(String(e.detail.value || ""))}
                    />
                  </IonItem>
                  <IonItem lines="none">
                    <IonButton
                      slot="start"
                      size="small"
                      fill="outline"
                      onClick={obtenerUbicacion}
                      disabled={obteniendoUbicacion}
                    >
                      <IonIcon slot="start" icon={locationOutline} />
                      {obteniendoUbicacion ? "Obteniendo..." : "Obtener ubicacion"}
                    </IonButton>
                    {obteniendoUbicacion && <IonSpinner slot="end" />}
                  </IonItem>
                  {latitud !== undefined && longitud !== undefined && (
                    <IonItem lines="none">
                      <IonLabel className="ion-text-wrap">
                        <IonText color="medium">
                          Ubicacion: {latitud.toFixed(6)}, {longitud.toFixed(6)}
                        </IonText>
                      </IonLabel>
                    </IonItem>
                  )}
                  <IonItem>
                    <IonLabel position="stacked">Contacto</IonLabel>
                    <IonInput
                      value={prospectoContacto}
                      placeholder="Opcional"
                      onIonChange={(e) => setProspectoContacto(String(e.detail.value || ""))}
                    />
                  </IonItem>
                </>
              )}

              <IonItem>
                <IonLabel position="stacked">Fecha de Visita *</IonLabel>
                <IonDatetime
                  displayFormat="DD/MM/YYYY"
                  pickerFormat="DD/MM/YYYY"
                  cancelText="Cancelar"
                  doneText="Listo"
                  value={fechaVisita}
                  onIonChange={(e) => setFechaVisita(normalizarFecha(e.detail.value))}
                />
              </IonItem>
            </IonList>
          </IonCardContent>
        </IonCard>

        {preguntasEstructura.length > 0 && (
          <IonCard style={{ marginLeft: 4, marginRight: 4 }}>
            <IonCardHeader>
              <IonCardTitle color="primary">Evaluacion del estado de la estructura edilicia</IonCardTitle>
            </IonCardHeader>
            <IonCardContent style={{ paddingLeft: 12, paddingRight: 12 }}>
              {preguntasEstructura.map((p, i) => renderPregunta(p, i + 1))}
            </IonCardContent>
          </IonCard>
        )}

        {preguntasRelevamiento.length > 0 && (
          <IonCard style={{ marginLeft: 4, marginRight: 4 }}>
            <IonCardHeader>
              <IonCardTitle color="primary">Evaluacion del relevamiento</IonCardTitle>
            </IonCardHeader>
            <IonCardContent style={{ paddingLeft: 12, paddingRight: 12 }}>
              {preguntasRelevamiento.map((p, i) => renderPregunta(p, i + 1))}
            </IonCardContent>
          </IonCard>
        )}

        <IonCard>
          <IonCardHeader>
            <IonCardTitle color="primary">Plan de accion y comentarios (opcional)</IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            <IonList>
              <IonItem>
                <IonLabel position="stacked">Plan de accion</IonLabel>
                <IonTextarea
                  autoGrow
                  value={planAccion}
                  onIonChange={(e) => setPlanAccion(String(e.detail.value || ""))}
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">Fecha plan de accion</IonLabel>
                <IonDatetime
                  displayFormat="DD/MM/YYYY"
                  pickerFormat="DD/MM/YYYY"
                  cancelText="Cancelar"
                  doneText="Listo"
                  value={fechaPlan}
                  onIonChange={(e) => setFechaPlan(normalizarFecha(e.detail.value))}
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">Comentario general</IonLabel>
                <IonTextarea
                  autoGrow
                  value={comentarioGeneral}
                  onIonChange={(e) => setComentarioGeneral(String(e.detail.value || ""))}
                />
              </IonItem>
            </IonList>
          </IonCardContent>
        </IonCard>

        <IonCard>
          <IonCardHeader>
            <IonCardTitle color="primary">Fotos (opcional)</IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              style={{ display: "none" }}
              onChange={onSeleccionarFotos}
            />
            <input
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: "none" }}
              id="reconocimiento-camera-input"
              onChange={onSeleccionarFotos}
            />
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <IonButton size="small" onClick={() => document.getElementById("reconocimiento-camera-input")?.click()}>
                <IonIcon slot="start" icon={cameraOutline} />
                Tomar foto
              </IonButton>
              <IonButton size="small" fill="outline" onClick={() => fileInputRef.current?.click()}>
                <IonIcon slot="start" icon={imageOutline} />
                Elegir de galeria
              </IonButton>
            </div>
            {fotos.length > 0 && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
                  gap: 8,
                  marginTop: 12,
                }}
              >
                {fotos.map((f, idx) => (
                  <div key={idx} style={{ position: "relative", border: "1px solid #ddd", borderRadius: 4 }}>
                    <img
                      src={f.url_secure || f.data_url}
                      alt={f.nombre_archivo || `foto-${idx + 1}`}
                      style={{ width: "100%", height: 120, objectFit: "cover", display: "block" }}
                    />
                    <IonButton
                      size="small"
                      color="danger"
                      style={{ position: "absolute", top: 2, right: 2 }}
                      onClick={() => eliminarFoto(idx)}
                    >
                      <IonIcon slot="icon-only" icon={trashOutline} />
                    </IonButton>
                  </div>
                ))}
              </div>
            )}
          </IonCardContent>
        </IonCard>

        <div className="ion-padding">
          <IonButton expand="block" onClick={onGuardar} disabled={guardando}>
            {guardando ? "Guardando..." : "Guardar"}
          </IonButton>
        </div>

        <IonCard>
          <IonCardHeader>
            <IonCardTitle color="primary">Ultimos reconocimientos</IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            {listando ? (
              <IonSpinner />
            ) : registros.length === 0 ? (
              <IonText color="medium">Sin registros.</IonText>
            ) : (
              <IonList>
                {registros.map((r) => (
                  <IonItem key={r.id} lines="full">
                    <IonLabel className="ion-text-wrap">
                      <div>
                        <strong>{r.ps_nombre || r.ps_codigo}</strong>
                        {r.es_prospecto ? <IonBadge color="tertiary" style={{ marginLeft: 6 }}>Prospecto</IonBadge> : null}
                      </div>
                      <div>{r.fecha_visita}</div>
                      <div>{r.fiscal_nombre || ""}</div>
                      <div>
                        <IonText color="medium">
                          Registrado por {r.creado_por_usuario || "-"} ({r.creado_en || "-"})
                        </IonText>
                      </div>
                    </IonLabel>
                    <IonBadge color="medium">{r.id}</IonBadge>
                  </IonItem>
                ))}
              </IonList>
            )}
          </IonCardContent>
        </IonCard>

        <IonToast
          isOpen={toast.show}
          message={toast.msg}
          color={toast.color}
          duration={2500}
          onDidDismiss={() => setToast({ show: false, msg: "" })}
        />
      </IonContent>
    </IonPage>
  );
};

export default EvaluacionReconocimiento;
